// ─────────────────────────────────────────────────────────────────────
// <AskPage /> — design vision spec (editorial assistant)
//
// Routes:
//   /ask           — fresh start, suggested prompts
//   /ask?q=<text>  — pre-fill the input + auto-submit
//
// SSE streaming via /api/ai/search?stream=1 (already wired in ai-service).
// ─────────────────────────────────────────────────────────────────────

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';

import api from '../api/client';
import SMark from '../components/ai/SMark';
import EventCard from '../components/event/EventCard';

const SUGGESTIONS = [
  'Comedy in Bengaluru this weekend',
  'Quiet classical evening, under ₹1000',
  'Family day out tomorrow',
  'Indie music tonight near MG Road',
  'Devotional event this Sunday',
];

export default function AskPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: t('ask.welcome', 'Tell me what kind of evening you\'re looking for. Be specific or vague — "comedy under 500" works, so does "something my mother would enjoy".'),
    },
  ]);
  const [input, setInput] = useState(params.get('q') || '');
  const [busy, setBusy]   = useState(false);
  const scrollRef = useRef(null);

  // Auto-submit if ?q= is set
  useEffect(() => {
    const q = params.get('q');
    if (q && !busy && messages.length === 1) {
      setInput('');
      handleSend(q);
      // Clear q from URL
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  const sessionRef = useRef(null);

  /**
   * r6: agentic Ask S. POST /api/ai/chat streams SSE:
   *   event: session {session_id} · event: tool {name} · event: token {chunk} · event: action {type,…} · event: done · event: error
   * Actions: { type:'checkout', booking, paymentOrder } → /checkout/:id ;  { type:'navigate', to } → navigate(to)
   * fetch + ReadableStream (EventSource cannot POST or send the bearer token).
   */
  const handleSend = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'you', text: q }]);
    setMessages((m) => [...m, { role: 'bot', text: '', streaming: true, results: null, tools: [] }]);
    const patchLast = (fn) => setMessages((m) => { const copy = m.slice(); copy[copy.length - 1] = fn(copy[copy.length - 1]); return copy; });

    try {
      const { tokenStore } = await import('../api/client');
      const res = await fetch(`${import.meta.env.VITE_API_BASE || '/api'}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(tokenStore.access ? { Authorization: `Bearer ${tokenStore.access}` } : {}) },
        body: JSON.stringify({ message: q, session_id: sessionRef.current, surface: 'ask_page', city: localStorage.getItem('snt_city') || undefined }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
      const handle = (ev, data) => {
        if (ev === 'session') sessionRef.current = data.session_id;
        else if (ev === 'token') patchLast((l) => ({ ...l, text: (l.text || '') + (data.chunk || '') }));
        else if (ev === 'tool') patchLast((l) => ({ ...l, tools: [...(l.tools || []), data.name] }));
        else if (ev === 'action') {
          if (data.type === 'checkout' && data.booking?.id) navigate(`/checkout/${data.booking.id}`, { state: { paymentOrder: data.paymentOrder || null } });
          else if (data.type === 'navigate' && typeof data.to === 'string' && data.to.startsWith('/')) navigate(data.to);
          else if (data.type === 'requires_login') navigate(`/login?next=${encodeURIComponent('/ask')}`);
        }
        else if (ev === 'done') patchLast((l) => ({ ...l, streaming: false, results: data.events || l.results || [] }));
        else if (ev === 'error') patchLast((l) => ({ ...l, streaming: false, text: l.text || t('ask.error', 'Sorry — I had trouble understanding that. Try a different phrasing?') }));
      };
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
          let ev = 'message'; const dataLines = [];
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) ev = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let data = {}; try { data = JSON.parse(dataLines.join('\n')); } catch { data = { chunk: dataLines.join('\n') }; }
          handle(ev, data);
        }
      }
      patchLast((l) => ({ ...l, streaming: false }));
    } catch (err) {
      // Fallback: non-agentic search (older gateway / offline AI)
      try {
        const { data } = await api.post('/ai/search', { query: q });
        patchLast(() => ({ role: 'bot', text: data.summary || data.answer || '', results: data.events || [], streaming: false }));
      } catch (err2) {
        // SNT-010: say what actually went wrong (gateway / AI service reason) instead of a generic apology
        const reason = err2?.response?.data?.message || err2?.response?.data?.error || err2?.message || '';
        patchLast(() => ({ role: 'bot', text: `${t('ask.error', 'Sorry — I had trouble with that.')}${reason ? ` (${reason})` : ''} ${t('ask.error_retry', 'Try a different phrasing?')}`, streaming: false }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="snt-container py-9">
      <div className="flex items-center gap-4 mb-7">
        <SMark size="lg" pulse glow />
        <div>
          <div className="font-serif text-[28px] font-light" style={{ color: 'var(--snt-text)' }}>
            {t('ask.title_1', 'Ask ')}
            <span className="snt-em-accent">S.</span>
          </div>
          <div className="text-[12px]" style={{ color: 'var(--snt-text-3)' }}>
            {t('ask.subtitle', 'Conversational search. Powered by Claude.')}
          </div>
        </div>
      </div>

      <div
        className="rounded-frame border border-line overflow-hidden flex flex-col"
        style={{ background: 'var(--snt-bg-1)', minHeight: 480 }}
      >
        {/* Stream */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-7"
          style={{ maxHeight: 600 }}
        >
          {messages.map((m, i) => (
            <div key={i} className="mb-5">
              {m.role === 'you' ? (
                <div className="snt-bubble-you ml-auto max-w-[75%]">{m.text}</div>
              ) : (
                <div className="max-w-[80%]">
                  {m.streaming && !m.text ? (
                    <div className="snt-bubble-bot">
                      <span className="inline-flex gap-1">
                        <Dot delay={0} />
                        <Dot delay={150} />
                        <Dot delay={300} />
                      </span>
                    </div>
                  ) : (
                    <div className="snt-bubble-bot">{m.text}</div>
                  )}
                  {m.tools?.length > 0 && <div className="text-[11px] text-ink-500 mt-1">{m.tools.map((x, k) => <span key={k} className="mr-2">⚙ {x.replace(/_/g, ' ')}</span>)}</div>}
                  {m.results?.length > 0 && (
                    <div className="mt-3 pl-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
                      {m.results.slice(0, 6).map((r) => (
                        <Link
                          key={r.id}
                          to={`/e/${r.slug || r.id}`}
                          className="block rounded-card p-3 no-underline transition-colors hover:border-accent"
                          style={{ background: 'var(--snt-bg-2)', border: '1px solid var(--snt-line)' }}
                        >
                          <div className="font-serif text-[13px] mb-1" style={{ color: 'var(--snt-text)' }}>
                            {r.title}
                          </div>
                          <div className="text-[10px]" style={{ color: 'var(--snt-text-3)' }}>
                            {r.venue_name} · {r.date_label}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Initial suggestion chips (only shown before any message) */}
          {messages.length === 1 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleSend(s)}
                  className="snt-chip"
                  style={{ fontSize: 11, padding: '6px 12px' }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          className="border-t border-line p-5 flex gap-3 items-center"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('ask.placeholder', 'Ask anything…')}
            className="snt-input flex-1"
            disabled={busy}
            autoFocus
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="rounded-full flex items-center justify-center transition-colors"
            style={{
              width: 42,
              height: 42,
              background: 'var(--snt-accent)',
              color: 'var(--snt-accent-fg)',
              border: 'none',
              cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
              opacity: busy || !input.trim() ? 0.5 : 1,
            }}
            aria-label={t('ask.send', 'Send')}
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full"
      style={{
        background: 'var(--snt-accent)',
        animation: `snt-dot-pulse 1.2s ease-in-out infinite`,
        animationDelay: `${delay}ms`,
      }}
    >
      <style>{`
        @keyframes snt-dot-pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50%      { opacity: 1;   transform: scale(1.0); }
        }
      `}</style>
    </span>
  );
}
